import React from 'react'
import update from 'immutability-helper'
import classnames from 'classnames'
import Dropzone from 'react-dropzone'
import { CSSTransitionGroup } from 'react-transition-group'
import { BaseComponent, Icon } from 'belle-js'
import NavLink from 'redux-first-router-link'
import File from './File'
import { trackAnalyticsEvent } from 'analytics'

import './DocumentUploader.scss'
const acceptedFileTypes = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/msword',
  '.key',
  '.pages',
  '.numbers'
].join(',')

const uploadErrorMessage = `Each file must be less than 20MB, and be a 
.pdf, .jpg, .png, .xlsx, .csv, .ppt, .key, .numbers, .pages, or .doc format.`

class DocumentUploader extends BaseComponent {

  constructor (props) {
    super(props)

    this.bind(
      'handleOnDragEnter',
      'handleOnDragLeave',
      'handleOnDrop',
      'handleOpenFileBrowser',
      'handleDismissClientError',
      'handleDismissFilLimitError',
      'handleUploadFiles',
      'handleResetUploader',
      'handleDismissServerError'
    )

    this.state = {
      isDragging: false,
      acceptedFiles: [],
      rejectedFiles: [],
      uploadingFiles: [],
      uploadedFiles: [],
      showClientError: false,
      showServerError: false,
      showFileLimitError: false,
      uploadComplete: false,
    }
  }

  handleDismissServerError () {
    this.setState({
      showServerError: false,
    })
  }

  handleOnDragEnter () {
    if (!this.state.uploadComplete) {
      this.setState({
        isDragging: true,
      })
    }
  }

  handleOnDragLeave () {
    if (!this.state.uploadComplete) {
      this.setState({
        isDragging: false,
      })
    }
  }

  removeFile (i) {
    const {
      acceptedFiles
    } = this.state
    const newFilesUpdated = update(acceptedFiles, {
      $splice: [[i, 1]],
    })

    this.setState({
      acceptedFiles: newFilesUpdated,
    })
  }

  handleDismissClientError () {
    this.setState({
      showClientError: false,
    })
  }

  handleDismissFilLimitError () {
    this.setState({
      showFileLimitError: false,
    })
  }

  handleOnDrop (incomingAcceptedFiles, rejectedFiles) {
    const {
      uploadComplete,
      acceptedFiles,
    } = this.state

    if (!uploadComplete) {
      const numberAcceptedFiles = acceptedFiles.length + incomingAcceptedFiles.length
      if (numberAcceptedFiles > 20) {
        this.setState({
          isDragging: false,
          showFileLimitError: true,
        })
      } else {
        const newAcceptedFiles = update(acceptedFiles, {
          $push: incomingAcceptedFiles,
        })

        this.setState({
          isDragging: false,
          acceptedFiles: newAcceptedFiles,
          showClientError: rejectedFiles.length > 0,
        })
      }
    }
  }

  handleOpenFileBrowser () {
    const {
      uploadComplete
    } = this.state
    if (!uploadComplete) {
      this.dropzoneRef.open()
    }
  }

  handleUploadFiles () {
    const {
      acceptedFiles,
      uploadComplete,
    } = this.state

    if (!uploadComplete) {
      this.setState({
        isUploading: true,
      })

      const { uploadFile } = this.props

      // Async/Await file upload
      const handleFileUpload = async (files) => {
        const uploadedFiles = []
        const failedFiles = []

        this.setState({
          uploadingFiles: files.map(f => f.name)
        })

        await Promise.all(files.map(async (file, i) => {
          let newState = {}
          try {
            await uploadFile(file)
            uploadedFiles.push(file.name)
            newState = {
              uploadedFiles,
              uploadComplete: true
            }
          } catch (error) {
            failedFiles.push(file.name)
            newState = {
              failedFiles,
              uploadComplete: false
            }
          } finally {
            this.setState(newState)
          }
        }))

        this.setState(prevState => ({
          isUploading: false,
          showServerError: failedFiles.length > 0,
          uploadedFiles: prevState.uploadedFiles.filter(f => !failedFiles.includes(f.name)),
          uploadingFiles: []
        }))
      }
      handleFileUpload(this.filterDuplicateFiles(acceptedFiles))
    }
  }

  filterDuplicateFiles (files) {
    return files.filter((file, pos, arr) => arr.map(f => f.name).indexOf(file.name) === pos)
  }

  handleResetUploader () {
    this.setState({
      isDragging: false,
      isUploading: false,
      acceptedFiles: [],
      rejectedFiles: [],
      uploadingFiles: [],
      uploadedFiles: [],
      showClientError: false,
      showFileLimitError: false,
      uploadComplete: false,
    })
  }

  render () {
    const maxFileSize = 20 * 1000 * 1000 // 20MB

    const {
      doneLink
    } = this.props

    const {
      acceptedFiles,
      uploadingFiles,
      uploadedFiles,
      isUploading,
    } = this.state

    const overlayStyles = classnames(
      'overlay',
      {
        'overlay--error': this.state.showClientError,
      }
    )

    const dropzoneStyles = classnames(
      'dropzone',
      {
        'dropzone--disabled': this.state.uploadComplete,
      }
    )

    const buttonStyles = classnames(
      'button',
      'button--primary',
      'uploadButton',
      {
        'is-disabled': acceptedFiles.length < 1 || isUploading,
      }
    )

    const overlayErrorButtonClasses = classnames(
      'button',
      'button--secondary',
      'dismissErrorButton'
    )

    const defaultCompleteLink = (<a href="https://www.ellevest.com" className={buttonStyles}>Done</a>)
    const customLink = (
      <NavLink to={doneLink} className={buttonStyles}>
        Back to My Uploads
      </NavLink>
    )
    const completeLink = doneLink ? customLink : defaultCompleteLink
    const afterUploadActionsContent = (
      <div>
        <button
          className="button button--secondary"
          data-test-id="secureDocUploader__uploadMoreButton"
          type="button"
          onClick={this.handleResetUploader}>
          Upload another Document
        </button>
        {completeLink}
      </div>
    )

    const documentUploaderClasses = classnames('DocumentUploader', this.props.className)

    return (
      <Dropzone
        ref={(node) => { this.dropzoneRef = node }}
        disableClick={true}
        className="fullscreen__dropzone Uploader"
        accept={acceptedFileTypes}
        maxSize={maxFileSize}
        onDragEnter={this.handleOnDragEnter}
        onDragLeave={this.handleOnDragLeave}
        onDrop={this.handleOnDrop}>
        <main className={documentUploaderClasses} data-test-id="documentUploader">
          <div className="container">
            {this.state.uploadComplete ? (
              <h1>Document Uploaded</h1>
            ) : (
              <h1>Secure Document Uploader</h1>
            )}

            <div className={dropzoneStyles}>
              <ul className="droppedFiles">
                {acceptedFiles.map((f, i) => (
                  <File
                    key={`file_${i}`}
                    file={f}
                    uploading={uploadingFiles.indexOf(f.name) > -1}
                    uploaded={uploadedFiles.indexOf(f.name) > -1}
                    canRemoveFile={!this.state.uploadComplete}
                    removeFile={(i) => {this.removeFile(i)}} />
                ))}
              </ul>

              {acceptedFiles.length < 1 && (
                <div className="dragInstructions">
                  <p className="dragFilesHere">
                     Drag and drop a file here
                  </p>
                  <p className="orAddFiles">
                    {'Or click to upload'}
                    <button
                      className="addFilesButton"
                      data-test-id="secureDocUploader__addFilesButton"
                      type="button"
                      onClick={() => {
                        this.handleOpenFileBrowser()
                        trackAnalyticsEvent('Add Files Clicked')
                      }}>
                      <Icon icon="add_15" />
                    </button>
                  </p>
                </div>
              )}
            </div>

            {this.state.uploadComplete ? (
              <div className="uploadActions">
                {afterUploadActionsContent}
              </div>
            ) : (
              <div className="uploadActions">
                <button
                  className={buttonStyles}
                  disabled={isUploading}
                  data-test-id="secureDocUploader__uploadButton"
                  type="button"
                  onClick={() => {
                    this.handleUploadFiles()
                    trackAnalyticsEvent('Upload Document Clicked')
                  }}>
                  Upload
                </button>
              </div>
            )}
          </div>

          <CSSTransitionGroup
            transitionName="uploaderOverlayTransition"
            transitionEnterTimeout={500}
            transitionLeaveTimeout={500}>
            {this.state.isDragging && (
              <div key="overlay_dragging" className={overlayStyles}>
                <p>
                  <Icon icon="add_30_filled" />
                  Drop files here
                </p>
              </div>
            )}

            {this.state.showClientError && (
              <div key="overlay_clientError" className={overlayStyles}>
                <p>Upload Failed</p>
                <p>
                  {uploadErrorMessage}
                  <button
                    className={overlayErrorButtonClasses}
                    data-test-id="secureDocUploader__error__gotItButton"
                    type="button"
                    onClick={this.handleDismissClientError}>
                    Try again
                  </button>
                </p>
              </div>
            )}

            {this.state.showFileLimitError && (
              <div key="overlay_clientError" className={overlayStyles}>
                <p>{'Oops! You can only upload up to 20 files at a time.'}
                  <button
                    className={overlayErrorButtonClasses}
                    data-test-id="secureDocUploader__error__gotItButton"
                    type="button"
                    onClick={this.handleDismissFilLimitError}>
                    Got It!
                  </button>
                </p>
              </div>
            )}

            {this.state.showServerError && (
              <div key="overlay_serverError" className={overlayStyles}>
                <div>
                  <p>
                   Upload failed
                  </p>
                  <p>
                    Oops! Something went wrong.
                  </p>
                  <button
                    type="button"
                    className={overlayErrorButtonClasses}
                    onClick={this.handleDismissServerError}>
                    Try again
                  </button>
                </div>
              </div>
            )}

          </CSSTransitionGroup>

        </main>
      </Dropzone>
    )
  }

}

export default DocumentUploader



// WEBPACK FOOTER //
// ./src/components/Shared/DocumentUploader/DocumentUploader.jsx